import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UserPlus, KeyRound } from 'lucide-react';
import { customers as api } from '../api';
import { CUSTOMER_ROLE_LABELS } from '../types';
import type { Customer, CustomerRole, CustomerUser } from '../types';

const ROLES: CustomerRole[] = ['ACCOUNT_OWNER', 'BILLING_USER', 'TECHNICAL_USER', 'READ_ONLY'];

/**
 * The logins this customer uses in the Customer Center. Kept separate from the
 * PC's own staff users — these accounts have no access to the Provisioning
 * Center whatsoever.
 */
export default function CustomerUsersPanel({ customer }: { customer: Customer }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [inviting, setInviting] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', role: 'READ_ONLY' as CustomerRole });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const { data: users, isLoading } = useQuery({
    queryKey: ['customers', customer.id, 'users'],
    queryFn: () => api.getUsers(customer.id),
  });

  function handleError(fallback: string) {
    return (err: unknown) => {
      setError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? fallback,
      );
      setMessage('');
    };
  }

  function refresh(msg: string) {
    return () => {
      qc.invalidateQueries({ queryKey: ['customers', customer.id, 'users'] });
      setError('');
      setMessage(msg);
    };
  }

  const inviteMutation = useMutation({
    mutationFn: () => api.inviteUser(customer.id, form),
    onSuccess: () => {
      setInviting(false);
      setForm({ name: '', email: '', role: 'READ_ONLY' });
      refresh('Einladung versendet.')();
    },
    onError: handleError('Einladen fehlgeschlagen.'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ userId, payload }: { userId: string; payload: Parameters<typeof api.updateUser>[2] }) =>
      api.updateUser(customer.id, userId, payload),
    onSuccess: refresh('Änderung gespeichert.'),
    onError: handleError('Speichern fehlgeschlagen.'),
  });

  const resetMutation = useMutation({
    mutationFn: (userId: string) => api.sendUserPasswordReset(customer.id, userId),
    onSuccess: refresh('Passwort-Link versendet.'),
    onError: handleError('Versand fehlgeschlagen.'),
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
          Kundenzugänge
        </h4>
        <button
          onClick={() => { setInviting((v) => !v); setError(''); setMessage(''); }}
          className="inline-flex items-center gap-1 text-xs text-brand-600 hover:text-brand-800 font-medium"
        >
          <UserPlus size={12} /> Einladen
        </button>
      </div>

      {error && <div className="text-xs text-red-600 mb-2">{error}</div>}
      {message && <div className="text-xs text-green-700 mb-2">{message}</div>}

      {inviting && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setError('');
            inviteMutation.mutate();
          }}
          className="bg-surface border border-slate-200 rounded-lg p-3 mb-2 space-y-2"
        >
          <div className="grid grid-cols-2 gap-2">
            <input
              required
              placeholder="Name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="border border-slate-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            <input
              required
              type="email"
              placeholder="E-Mail"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              className="border border-slate-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <div className="flex gap-2">
            <select
              value={form.role}
              onChange={(e) => setForm((f) => ({ ...f, role: e.target.value as CustomerRole }))}
              className="flex-1 border border-slate-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-brand-500"
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>{CUSTOMER_ROLE_LABELS[r]}</option>
              ))}
            </select>
            <button
              type="submit"
              disabled={inviteMutation.isPending}
              className="bg-brand-600 hover:bg-brand-700 text-white text-xs font-medium px-3 py-1.5 rounded disabled:opacity-60"
            >
              {inviteMutation.isPending ? 'Sendet…' : 'Einladen'}
            </button>
          </div>
          <p className="text-xs text-slate-400">
            Es wird kein Passwort vergeben — der Kunde bekommt einen Link zum Selbstsetzen.
          </p>
        </form>
      )}

      {isLoading ? (
        <div className="h-8 bg-slate-100 rounded animate-pulse" />
      ) : !users?.length ? (
        <p className="text-xs text-slate-400 italic">{t('customers.noLogins')}</p>
      ) : (
        <div className="space-y-1.5">
          {users.map((user: CustomerUser) => (
            <div
              key={user.id}
              className="bg-surface border border-slate-200 rounded-lg px-3 py-2 flex items-center justify-between gap-3"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-medium ${user.status === 'active' ? 'text-slate-900' : 'text-slate-400 line-through'}`}>
                    {user.name}
                  </span>
                  {!user.lastLoginAt && (
                    <span className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 px-1.5 rounded-full">
                      noch nie angemeldet
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500 truncate">{user.email}</div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <select
                  value={user.role}
                  onChange={(e) =>
                    updateMutation.mutate({
                      userId: user.id,
                      payload: { role: e.target.value as CustomerRole },
                    })
                  }
                  className="border border-slate-300 rounded px-1.5 py-1 text-xs"
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>{CUSTOMER_ROLE_LABELS[r]}</option>
                  ))}
                </select>

                {user.status === 'active' && customer.status === 'ACTIVE' && (
                  <button
                    onClick={() => resetMutation.mutate(user.id)}
                    title={t('customers.sendPasswordLink')}
                    className="text-slate-400 hover:text-slate-700 p-1"
                  >
                    <KeyRound size={13} />
                  </button>
                )}

                <button
                  onClick={() =>
                    updateMutation.mutate({
                      userId: user.id,
                      payload: { status: user.status === 'active' ? 'inactive' : 'active' },
                    })
                  }
                  className={`text-xs font-medium ${
                    user.status === 'active'
                      ? 'text-red-600 hover:text-red-800'
                      : 'text-green-600 hover:text-green-800'
                  }`}
                >
                  {user.status === 'active' ? 'Deaktivieren' : 'Aktivieren'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
